import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { ListChecks, ShieldAlert } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useModule } from '../../../lib/modules';
import { registerCommands } from '../../../shell/command-registry';
import { useSortingOverview } from '../api/sorting-api';
import styles from './SortingEntry.module.css';

/**
 * JOB-12's way into SCR-01 and SCR-04 (SCR-01 §2, enabled only when there is something to
 * review). R6: sorting mode is HR's review card, so it reads "Start review" (§L31). Also
 * offered in the command palette while this job is open (06-qol §1: context comes first).
 */
export function SortingEntry({ jobId, jobTitle }: { readonly jobId: string; readonly jobTitle: string }): ReactNode {
  const overview = useSortingOverview(jobId);
  const data = overview.data;
  const canSort = data?.canSort === true;
  const navigate = useNavigate();
  // R1 (§L34): knockouts come from the career site's apply form, so their review goes with it.
  const isCareerSiteOn = useModule('career_site');
  // Shown on ③ Ranking and ④ Review only, so this is where the card's Exit comes back to.
  const here = useRouterState({ select: (state) => state.location.href });

  useEffect(() => {
    if (!canSort) return undefined;
    return registerCommands([
      {
        id: `sorting.start.${jobId}`,
        label: 'Start review',
        section: 'action',
        context: jobTitle,
        permission: 'screening_decision',
        icon: ListChecks,
        keywords: ['review', 'sort', 'screen', 'shortlist', 'cvs', 'queue', 'triage'],
        run: () => void navigate({ to: '/jobs/$jobId/sort', params: { jobId } }),
      },
    ]);
  }, [canSort, jobId, jobTitle, navigate]);

  if (!data) return null;
  return (
    <div className={styles.entry}>
      {isCareerSiteOn && data.autoRejectedCount > 0 && (
        <Link to="/jobs/$jobId/auto-rejected" params={{ jobId }} className={styles.autoRejected}>
          <ShieldAlert size={14} aria-hidden />
          {data.autoRejectedCount} auto-rejected
          {data.autoRejectedPercent !== null ? ` · ${data.autoRejectedPercent}%` : ''}
        </Link>
      )}
      {canSort ? (
        <Link to="/jobs/$jobId/sort" params={{ jobId }} state={{ reviewFrom: here }} className={styles.start}>
          <ListChecks size={16} aria-hidden />
          Start review
          <span className={styles.count}>{data.queueCount}</span>
        </Link>
      ) : (
        data.queueCount === 0 && data.applicationCount > 0 && <span className={styles.clear}>Nobody waiting for review</span>
      )}
    </div>
  );
}
